import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { UncontrolledPopover, PopoverHeader, PopoverBody } from 'reactstrap';
import { useParams } from 'react-router-dom';
import _ from 'underscore';

import services from 'api/services';
import { Button } from '../../components';
import { useActions } from 'hooks/useActions';
import * as actions from './actions';
import {
  QuestionAndOptionsWrapper,
  QuestionNumber,
  QuestionSection,
  OptionsContainer,
  NavigationButtonsComponent,
  QuestionValue,
  OptionBox,
  OptionValue,
  ParagraphComponent,
  ParagraphAndQuestionCountComponent,
  OptionChar,
} from './style';

const { InputService } = services;

export default React.memo(({ setInactiveTime }) => {
  const { ticketId } = useParams();
  const [isExpanded, setIsExpanded] = useState(false);
  const [hideSkipInfo, setHideSkipInfo] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdaptiveTest, setIsAdaptiveTest] = useState(true);
  const [paragraphQuestion, setParagraphQuestion] = useState({});

  let blurTimeRef = useRef(new Date());
  let inactiveTimeRef = useRef(0);
  let questionRef = useRef(null);
  let optionsRef = useRef(null);

  const { updateInput, setCurrentQuestionObject, setCurrentOptionId } = useActions(actions);
  const { ticketResponse, loading, currentQuestionObject, currentOptionId, nonAdaptiveQuestionObject } = useSelector(
    (state) => state.multichoiceTestPage
  );
  let { ticket, test } = ticketResponse;

  // Usage: Both type of tests
  // Start of common useEffects

  useEffect(() => {
    const updateInactiveTime = () => {
      let time = new Date() - blurTimeRef.current;
      inactiveTimeRef.current += time;
      setInactiveTime(inactiveTimeRef.current);
    };

    const updateActiveTime = () => {
      blurTimeRef.current = new Date();
    };

    window.addEventListener('focus', updateInactiveTime);
    window.addEventListener('blur', updateActiveTime);

    return () => {
      window.removeEventListener('focus', updateInactiveTime);
      window.removeEventListener('blur', updateActiveTime);
    };
  }, []);

  useEffect(() => {
    setIsAdaptiveTest(test.adaptiveTest);
    setParagraphQuestion(ticket.paragraphWiseQuestion);
  }, [ticketResponse]);

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  useEffect(() => {
    if (!currentQuestionObject) return;

    const updateCopiedQuestionId = () => {
      InputService.updateEvent(ticketId, currentQuestionObject.question._id, { question: true });
    };
    const updateCopiedOptionId = (optionId) => {
      InputService.updateEvent(ticketId, currentQuestionObject.question._id, { option: optionId });
    };
    const updateRightClickEvent = (event) => {
      if (event.target.tagName === 'IMG')
        InputService.updateEvent(ticketId, currentQuestionObject.question._id, { isRightClickOnImage: true });
      else InputService.updateEvent(ticketId, currentQuestionObject.question._id, { isRightClick: true });
    };

    const stripStringForCopyCompare = (htmlString) =>
      htmlString
        .replace(/<[^>]*>|\s+/g, '')
        .toString()
        .trim();

    const checkSelectedText = () => {
      let selectedText = stripStringForCopyCompare(window.getSelection().toString());
      if (!selectedText) return;
      let questionValue = stripStringForCopyCompare(currentQuestionObject.question.value);
      let optionsSelected = [];
      // Use for in loop and _.size since currentQuestionObject.options can be array or object depending on adaptive or not
      for (const optionKey in currentQuestionObject.options) {
        const option = currentQuestionObject.options[optionKey];
        let currentOption = stripStringForCopyCompare(option.value);
        if (selectedText.includes(currentOption)) optionsSelected.push(option._id);
      }
      if (selectedText.includes(questionValue) && optionsSelected.length === _.size(currentQuestionObject.options))
        return InputService.updateEvent(ticketId, currentQuestionObject.question._id, { isAllTextCopied: true });

      if (selectedText.includes(questionValue)) updateCopiedQuestionId();
      for (const optionId of optionsSelected) updateCopiedOptionId(optionId);
    };

    setIsExpanded(false);

    const questionElement = questionRef.current;
    questionElement.addEventListener('copy', updateCopiedQuestionId);
    window.addEventListener('copy', checkSelectedText);
    window.addEventListener('contextmenu', updateRightClickEvent);
    optionsRef.current.forEach((optionElement) =>
      optionElement.addEventListener('copy', () => updateCopiedOptionId(optionElement.id))
    );

    return () => {
      questionElement.removeEventListener('copy', updateCopiedQuestionId);
      optionsRef.current.forEach((optionElement) =>
        optionElement.removeEventListener('copy', () => updateCopiedOptionId(optionElement.id))
      );
      window.removeEventListener('copy', checkSelectedText);
      window.removeEventListener('contextmenu', updateRightClickEvent);
    };
  }, [currentQuestionObject, ticketId]);

  const getMapOfOptionsRef = () => {
    if (!optionsRef.current) optionsRef.current = new Map();
    return optionsRef.current;
  };

  const getParagraph = () => {
    return (
      <>
        {!isAdaptiveTest && (
          <ParagraphAndQuestionCountComponent>
            <b>Paragraph: {paragraphQuestion[currentQuestionObject.paragraph._id]['paraNumber']}</b>
            <b>
              Question:
              {paragraphQuestion[currentQuestionObject.paragraph._id][currentQuestionObject.question._id]}/
              {paragraphQuestion[currentQuestionObject.paragraph._id]['count']}
            </b>
          </ParagraphAndQuestionCountComponent>
        )}
        <ParagraphComponent
          dangerouslySetInnerHTML={{
            __html: currentQuestionObject.paragraph.value.substring(
              0,
              isExpanded ? currentQuestionObject.paragraph.value.length : 700
            ),
          }}
        ></ParagraphComponent>
        {currentQuestionObject.paragraph.value.length > 700 && (
          <Button
            small
            textAlign="right"
            primaryBlue
            primaryGreenBorder
            removeMargin
            buttonClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </Button>
        )}
      </>
    );
  };

  const getOptionChar = (index) => {
    // CharCode is to get a), b) etc for all options
    return <OptionChar>{`${String.fromCharCode(97 + index)})`}</OptionChar>;
  };

  const getOptions = () => {
    if (!currentQuestionObject) return;
    return Object.values(currentQuestionObject.options).map((option, index) => {
      return (
        <OptionBox
          key={option._id}
          isSelected={option.isAnswered || currentOptionId === option._id}
          onClick={() => updateOption(option)}
        >
          {getOptionChar(index)}
          <OptionValue
            isSelected={option.isAnswered || currentOptionId === option._id}
            id={option._id}
            ref={(node) => {
              const map = getMapOfOptionsRef();
              if (node) map.set(option._id, node);
              else map.delete(option._id);
            }}
            dangerouslySetInnerHTML={{ __html: option.value }}
          ></OptionValue>
        </OptionBox>
      );
    });
  };

  const updateOption = (option) => {
    setCurrentOptionId(option._id);
    if (!isAdaptiveTest) {
      let input = {
        question: currentQuestionObject.question._id,
        option: option._id,
      };
      updateInput(ticketId, input, isAdaptiveTest);
      if (currentQuestionObject.questionNumber !== ticket.totalQuestions) {
        setCurrentQuestionObject(
          Object.values(nonAdaptiveQuestionObject).find(
            (element) => element.questionNumber === currentQuestionObject.questionNumber + 1
          )
        );
      } else {
        setCurrentQuestionObject(null);
      }
    }
  };

  const skipQuestion = () => {
    if (localStorage.getItem(`skipButtonPopup${ticketId}`)) {
      setHideSkipInfo(true);
      updateQuestionInput();
    } else {
      localStorage.setItem(`skipButtonPopup${ticketId}`, 'true');
      setHideSkipInfo(false);
    }
  };

  const updateQuestionInput = () => {
    if (isAdaptiveTest) {
      let input = {
        inactiveTime: inactiveTimeRef.current,
        question: currentQuestionObject.question._id,
        option: currentOptionId,
      };
      inactiveTimeRef.current = 0;
      blurTimeRef.current = new Date();
      updateInput(ticketId, input, isAdaptiveTest);
      setInactiveTime(0);
    } else {
      setCurrentQuestionObject(
        Object.values(nonAdaptiveQuestionObject).find(
          (element) => element.questionNumber === currentQuestionObject.questionNumber + 1
        )
      );
    }
  };

  const setPreviousQuestion = () => {
    setCurrentQuestionObject(
      Object.values(nonAdaptiveQuestionObject).find(
        (element) => element.questionNumber === currentQuestionObject.questionNumber - 1
      )
    );
  };

  const renderNavigationButtons = () => {
    return (
      <>
        {isAdaptiveTest ? (
          currentQuestionObject.questionNumber !== ticket.totalQuestions && (
            <>
              <Button
                id="PopoverLegacy"
                textAlign="left"
                zIndex="1"
                primaryBlue
                primaryGreenBorder
                removeMargin
                disabled={currentOptionId || isLoading}
                onBlur={() => setHideSkipInfo(true)}
                buttonClick={() => skipQuestion()}
              >
                <i className="fa fa-arrow-right">&nbsp;</i>
                Skip Question
              </Button>
              <UncontrolledPopover isOpen={!hideSkipInfo} trigger="legacy" placement="top" target="PopoverLegacy">
                <PopoverHeader>Warning</PopoverHeader>
                <PopoverBody>
                  Once you skip the question, you won't be allowed to return back to a previous question
                </PopoverBody>
              </UncontrolledPopover>
            </>
          )
        ) : (
          <Button
            textAlign="left"
            zIndex="1"
            primaryBlue
            primaryGreenBorder
            removeMargin
            disabled={currentQuestionObject.questionNumber === 1}
            buttonClick={() => setPreviousQuestion()}
          >
            <i className="fa fa-arrow-left">&nbsp;</i>Previous
          </Button>
        )}
        {currentQuestionObject.questionNumber !== ticket.totalQuestions && (
          <Button
            textAlign="right"
            zIndex="1"
            primaryBlue
            primaryGreenBorder
            removeMargin
            disabled={isLoading || (isAdaptiveTest ? !currentOptionId : false)}
            buttonClick={() => updateQuestionInput()}
          >
            <i className="fa fa-arrow-right">&nbsp;</i>Next
          </Button>
        )}
      </>
    );
  };

  return (
    <>
      {currentQuestionObject.paragraph && getParagraph()}
      <QuestionNumber>Question: {currentQuestionObject.questionNumber}</QuestionNumber>
      <QuestionAndOptionsWrapper id="questionOptionSection">
        <QuestionSection>
          <QuestionValue
            ref={questionRef}
            dangerouslySetInnerHTML={{
              __html: currentQuestionObject.question.value,
            }}
          ></QuestionValue>
        </QuestionSection>
        <OptionsContainer>{getOptions()}</OptionsContainer>
      </QuestionAndOptionsWrapper>
      <NavigationButtonsComponent>{renderNavigationButtons()}</NavigationButtonsComponent>
    </>
  );
});
